import * as React from 'react';
import camelCase from 'lodash/camelCase';
import isNil from 'lodash/isNil';

import { Form, FormActions, FormField } from 'components/form';
import Input from 'components/inputs/input/Input';
import ActionButton from 'components/buttons/action-button/ActionButton';
import MixedSelectDropdown from 'components/inputs/mixed-select-dropdown/MixedSelectDropdown';

import { __t, applyTranslations } from 'utils/language';
import {
  GENDER_ADDITIONAL_LABELS,
  GENDER_ADDITIONAL_OPTIONS,
  GENDER_LABELS,
  GENDER_OPTIONS
} from 'utils/influencers';
import { isRequired, maxLength, minLength } from 'utils/validators';

import './profile-creation-gender.scss';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('./profile-creation-gender.i18n.json'));

import type {
  Data,
  InfluencerProfileForm,
  MixedSelectDropdownItem,
  TextEntry
} from 'types';
import type {
  FieldDescriptionShowParams
} from 'modules/influencers/components/influencer-create-profile/InfluencerCreateProfile';

interface Props {
  details: InfluencerProfileForm,
  onSubmit: (details: InfluencerProfileForm) => void,
  onCancel: (details: InfluencerProfileForm) => void,
  onFieldDescriptionShow: (params: FieldDescriptionShowParams) => void,
  onFieldDescriptionHide: () => void
}

const COMMON_MSG_KEY = 'collabary_common';
const MSG_KEY = 'profile_creation_gender';
const MIN_LENGTH_SELF_DESCRIBE = 2;
const MAX_LENGTH_SELF_DESCRIBE = 20;

const ProfileCreationGender: React.FC<Props> = ({
  details,
  onSubmit,
  onCancel,
  onFieldDescriptionShow,
  onFieldDescriptionHide
}): JSX.Element => {
  const [updatedDetails, setUpdatedDetails] = React.useState(details);

  const getGenderItems = (): Array<MixedSelectDropdownItem> => (
    [...GENDER_OPTIONS].map(value => ({
      identifier: value,
      isDisabled: false,
      isSelected: updatedDetails.influencerGender === value,
      label: GENDER_LABELS[value],
      value
    }))
  );

  const getGenderAdditionalItems = (): Array<MixedSelectDropdownItem> => (
    [...GENDER_ADDITIONAL_OPTIONS].map(value => ({
      identifier: value,
      isDisabled: false,
      isSelected: updatedDetails.influencerGenderAdditional === value,
      label: GENDER_ADDITIONAL_LABELS[camelCase(value)],
      value
    }))
  );

  const getHelperText = (): React.ReactNode => {
    const getText = (key: string): React.ReactNode => (
      <div>
        {__t(`${MSG_KEY}.helper_text.${key}`)}
      </div>
    );

    return (
      <div className="profile-creation-gender__helper-text">
        {getText('block_one')}
        {getText('block_two')}
        {getText('block_three')}
      </div>
    );
  };

  const handleDropdownUpdate = (
    updatedItems: Array<MixedSelectDropdownItem>,
    data?: Data | null
  ): void => {
    if (isNil(data)) {
      return;
    }

    const selectedItem = updatedItems.find(item => item.isSelected);
    const type = data.type;

    setUpdatedDetails({
      ...updatedDetails,
      [type]: !isNil(selectedItem)
        ? selectedItem.identifier
        : updatedDetails[type]
    });
  };

  const handleFieldUpdate = (
    event: React.SyntheticEvent<TextEntry>
  ): void => {
    const target = event.currentTarget;
    const type = target.dataset?.type;
    const value = target.value ?? '';

    if (isNil(type)) {
      return;
    }

    setUpdatedDetails({
      ...updatedDetails,
      [type]: value
    });
  };

  const handleSubmit = (): void => (
    onSubmit(updatedDetails)
  );

  const handleCancel = (): void => (
    onCancel(updatedDetails)
  );

  const renderDropdown = (
    type: 'influencerGender' | 'influencerGenderAdditional',
    items: Array<MixedSelectDropdownItem>
  ): React.ReactNode => (
    <div className="profile-creation-gender__dropdown">
      <MixedSelectDropdown
        key={`profile-creation-gender__${type}-dropdown--${updatedDetails[type]}`}
        name={`profile-creation-gender__${type}-dropdown`}
        items={items}
        placeholder={__t(`${COMMON_MSG_KEY}.basics.choose_one`)}
        withSelectedInList
        onItemsToggle={handleDropdownUpdate}
        data={{ type }}
      />
    </div>
  );

  const renderInput = (): React.ReactNode => {
    const value = updatedDetails.influencerGenderSelfDescribed ?? '';

    return (
      <Input
        autoFocus
        value={value}
        name="influencerGenderSelfDescribed"
        className="profile-creation-gender__input"
        onChange={handleFieldUpdate}
        charCount={{
          curCount: value.length,
          maxChars: MAX_LENGTH_SELF_DESCRIBE,
          showCountOnly: true
        }}
        data={{ type: 'influencerGenderSelfDescribed' }}
      />
    );
  };

  const renderActions = (): React.ReactNode => (
    <FormActions align="center">
      <ActionButton
        color="tertiary"
        onClick={handleCancel}
      >
        {__t(`${COMMON_MSG_KEY}.action_button.back`)}
      </ActionButton>
      <ActionButton
        color="primary"
        type="submit"
      >
        {__t(`${COMMON_MSG_KEY}.action_button.continue`)}
      </ActionButton>
    </FormActions>
  );

  React.useEffect(() => {
    onFieldDescriptionShow({
      name: 'selfIdentity',
      positionTop: 84,
      width: 500,
      component: getHelperText()
    });

    return () => {
      onFieldDescriptionHide();
    };
  }, []);

  return (
    <div className="profile-creation-gender">
      <Form
        className="profile-creation-gender__form"
        onSubmit={handleSubmit}
      >
        <div className="profile-creation-gender__column">
          <FormField
            label={__t(`${MSG_KEY}.labels.identity`)}
            labelType="highlighted"
            direction="column"
            showRequiredMark
            showValidationIcon={false}
            validators={[
              isRequired(updatedDetails.influencerGender)
            ]}
          >
            {renderDropdown('influencerGender', getGenderItems())}
          </FormField>
        </div>
        <div className="profile-creation-gender__column">
          <FormField
            label={__t(`${MSG_KEY}.labels.specifics`)}
            labelType="highlighted"
            direction="column"
            showValidationIcon={false}
          >
            {renderDropdown('influencerGenderAdditional', getGenderAdditionalItems())}
          </FormField>
        </div>
        {
          updatedDetails.influencerGenderAdditional === 'self_describe' && (
            <div className="profile-creation-gender__column profile-creation-gender__column-self-describe">
              <FormField
                label={__t(`${MSG_KEY}.labels.identification`)}
                labelType="highlighted"
                direction="column"
                showRequiredMark
                showValidationIcon={false}
                validators={[
                  minLength(updatedDetails.influencerGenderSelfDescribed, MIN_LENGTH_SELF_DESCRIBE),
                  maxLength(updatedDetails.influencerGenderSelfDescribed, MAX_LENGTH_SELF_DESCRIBE)
                ]}
              >
                {renderInput()}
              </FormField>
            </div>
          )
        }
        {renderActions()}
      </Form>
    </div>
  );
};

export default ProfileCreationGender;
